/**
 * Visual helpers for the app-configurator menu editors.
 *
 * Pure functions used by MenuGrid, MenuTabsEditor, and PhoneScreen:
 *   - Color conversion (ARGB → rgba)
 *   - Submenu / widget / banner detection
 *   - Template-kind classification and context-based filtering
 *   - Layout math (column width, cell width/height, gap, padding)
 *   - Icon URL resolution (single-language and multi-language variants)
 *   - Style builders for cells, text, and phone bars
 *   - Font loading with deduplication
 */

/** Hover-hold duration (in ms) before a drag enters a submenu or pops back. */
export const HOVER_HOLD_TO_ENTER_MS = 1400

// Native-submenu row height (px at ratio 1). Native lists render as a vertical
// stack of compact rows — like iOS table cells — not as full-height grid cells.
export const NATIVE_ROW_HEIGHT = 44

// =============================================================================
// Enum-like constants
// =============================================================================
// Centralised here so typos are impossible and refactors stay refactor-safe.
// Keep the string values stable — they're matched against API payloads.

/**
 * Navigation context — the parent kind at the current drill-down level.
 * Used by `allowedTemplateKindsForContext` to filter create-time templates.
 */
export const CONTEXT_KINDS = Object.freeze({
    ROOT:           'root',
    HOME_SUBMENU:   'home-submenu',
    NATIVE_SUBMENU: 'native-submenu',
})

/**
 * Menu type as stored on the menu object (`menu.type`).
 */
export const MENU_TYPES = Object.freeze({
    HOME:     'home',
    TAB:      'tab',
    SELECTOR: 'selector',
    PROFILE:  'profile',
})

/**
 * Item-template classification used by context-based filtering.
 * Note camelCase: these strings are the keys of the object returned by
 * `allowedTemplateKindsForContext`, and the return value of `getTemplateKind`.
 */
export const TEMPLATE_KINDS = Object.freeze({
    LEAF:           'leaf',
    HOME_SUBMENU:   'homeSubmenu',
    NATIVE_SUBMENU: 'nativeSubmenu',
    BANNER:         'banner',
    WIDGET:         'widget',
})

/** Section name shared by all submenu items and submenu templates. */
export const SECTION_SUBMENU = 'submenu'

/** `configuration.type` values that distinguish submenu variants. */
export const SUBMENU_VARIANTS = Object.freeze({
    HOME:   'home',
    NATIVE: 'native',
})

const WIDGET_TYPES = ['widget_resort_state', 'widget_webcams', 'single_data_widget']
const BANNER_TYPES = ['banners']

// =============================================================================
// Color conversion
// =============================================================================

/**
 * Convert an ARGB color (numeric `0xAARRGGBB` or `"#AARRGGBB"` string) to a CSS
 * `rgba()` string. Returns `'transparent'` for `null`/`undefined`.
 *
 * @param {number|string|null|undefined} argb
 * @returns {string}
 */
export function argbToRgba(argb) {
    if (argb === null || argb === undefined) return 'transparent'
    const hex = typeof argb === 'string'
        ? argb.replace('#', '').padStart(8, '0')
        : argb.toString(16).padStart(8, '0')
    const a = parseInt(hex.slice(0, 2), 16) / 255
    const r = parseInt(hex.slice(2, 4), 16)
    const g = parseInt(hex.slice(4, 6), 16)
    const b = parseInt(hex.slice(6, 8), 16)
    if ([a, r, g, b].some(Number.isNaN)) return 'transparent'
    return `rgba(${r},${g},${b},${a.toFixed(3)})`
}

// =============================================================================
// Submenu / special-item detection (data-driven via item.section)
// =============================================================================

/** @param {object} item */
export function isSubmenuItem(item) {
    return !!item && item.section === SECTION_SUBMENU
}

/** @param {object} item */
export function isNativeSubmenuItem(item) {
    return isSubmenuItem(item) && !!item.configuration && item.configuration.type === SUBMENU_VARIANTS.NATIVE
}

/** @param {object} item */
export function isHomeSubmenuItem(item) {
    return isSubmenuItem(item) && !!item.configuration && item.configuration.type === SUBMENU_VARIANTS.HOME
}

/** @param {object} item */
export function isWidgetItem(item) {
    return !!item && WIDGET_TYPES.includes(item.section)
}

/** @param {object} item */
export function isBannerItem(item) {
    return !!item && BANNER_TYPES.includes(item.section)
}

/** @param {object} item */
export function isSpecialItem(item) {
    return isWidgetItem(item) || isBannerItem(item)
}

// =============================================================================
// Template classification and context-based filtering
// =============================================================================

/**
 * Classify a template into one of the kinds used by `allowedTemplateKindsForContext`.
 * @param {object} template
 * @returns {'leaf'|'homeSubmenu'|'nativeSubmenu'|'banner'|'widget'}
 */
export function getTemplateKind(template) {
    if (!template) return TEMPLATE_KINDS.LEAF
    if (template.section === SECTION_SUBMENU) {
        const cfg = template.configuration_default
        return (cfg && cfg.type === SUBMENU_VARIANTS.NATIVE)
            ? TEMPLATE_KINDS.NATIVE_SUBMENU
            : TEMPLATE_KINDS.HOME_SUBMENU
    }
    if (WIDGET_TYPES.includes(template.type)) return TEMPLATE_KINDS.WIDGET
    if (BANNER_TYPES.includes(template.type)) return TEMPLATE_KINDS.BANNER
    return TEMPLATE_KINDS.LEAF
}

/**
 * Which template kinds are allowed in a given navigation context. Applied on
 * CREATE only — EDIT keeps all templates so legacy data can be re-saved.
 * Tabs aren't in this enum; the menu-type filter alone constrains them.
 *
 * @param {'root'|'home-submenu'|'native-submenu'} contextKind
 * @returns {{leaf:boolean,homeSubmenu:boolean,nativeSubmenu:boolean,banner:boolean,widget:boolean}}
 */
export function allowedTemplateKindsForContext(contextKind) {
    const K = TEMPLATE_KINDS
    switch (contextKind) {
        case CONTEXT_KINDS.ROOT:
            return { [K.LEAF]: true, [K.HOME_SUBMENU]: true, [K.NATIVE_SUBMENU]: true,  [K.BANNER]: true,  [K.WIDGET]: true  }
        case CONTEXT_KINDS.HOME_SUBMENU:
            return { [K.LEAF]: true, [K.HOME_SUBMENU]: true, [K.NATIVE_SUBMENU]: true,  [K.BANNER]: true,  [K.WIDGET]: false }
        case CONTEXT_KINDS.NATIVE_SUBMENU:
            return { [K.LEAF]: true, [K.HOME_SUBMENU]: true, [K.NATIVE_SUBMENU]: false, [K.BANNER]: false, [K.WIDGET]: false }
        default:
            return { [K.LEAF]: true, [K.HOME_SUBMENU]: false, [K.NATIVE_SUBMENU]: false, [K.BANNER]: false, [K.WIDGET]: false }
    }
}

/**
 * Filter a templates array down to only those whose `getTemplateKind` is
 * allowed in the given context.
 *
 * @param {object[]} templates
 * @param {string} contextKind
 */
export function filterTemplatesForContext(templates, contextKind) {
    const allowed = allowedTemplateKindsForContext(contextKind)
    return templates.filter(t => allowed[getTemplateKind(t)])
}

// =============================================================================
// Layout math
// =============================================================================
// All accept a pre-computed ratio (containerWidth / BASE_WIDTH).
// Formulas replicate MenuPreview.vue:183-261, generalised for any container width.

const REFERENCE_BASE_WIDTH = 375  // legacy iPhone 8 logical width used by the layout values

/** ratio = containerWidth / 375 (the reference width baked into layout values). */
export function computeRatio(containerWidth) {
    return containerWidth / REFERENCE_BASE_WIDTH
}

export function getEffectiveWidth(layout, ratio) {
    return ratio * (REFERENCE_BASE_WIDTH - layout.padding * 2)
}

export function getGapWidth(layout, ratio) {
    return layout.spacing * ratio
}

export function getHomePadding(layout, ratio) {
    return layout.padding * ratio
}

export function getColumnWidth(layout, ratio) {
    const effective = getEffectiveWidth(layout, ratio)
    const gap = getGapWidth(layout, ratio)
    return (effective - gap * (layout.columns_number - 1)) / layout.columns_number
}

/** Width in pixels for an item spanning `cols` columns. */
export function widthFor(cols, layout, ratio) {
    const colWidth = getColumnWidth(layout, ratio)
    const gap = getGapWidth(layout, ratio)
    return colWidth * cols + gap * (cols - 1)
}

/**
 * Height in pixels for an item spanning `rows` rows (default 1).
 * Multi-row items consume both the underlying row heights AND the inter-row gaps.
 */
export function heightFor(rows = 1, layout, ratio) {
    return layout.height * rows * ratio + (rows - 1) * layout.spacing * ratio
}

// =============================================================================
// Icon URL resolution
// =============================================================================

/**
 * Resolve an item's `icon` key to a CDN URL from the platform resources.
 * Mirrors `ManageMenuItemForm.imagesObject` (single-image vs multi-language).
 *
 * @param {object}   item                   menu item with an `icon` string key
 * @param {string[]} resources              full URLs from version.resourcesByPlatformAndTimeofyear
 * @param {object[]} languages              array of { acronym }
 * @param {string}   mainLanguageAcronym    preferred acronym
 * @returns {string|null}
 */
export function resolveIconUrl(item, resources, languages, mainLanguageAcronym) {
    const iconKey = item && item.icon
    if (!iconKey) return null

    // 1. Single-image match: /<icon>.<ext>
    const singleRegex = new RegExp(`/${iconKey}\\.`)
    const singleMatches = resources.filter(r => singleRegex.test(r))
    if (singleMatches.length > 0) return singleMatches[0]

    // 2. Multi-language match: /<icon>_(<acronym>).<ext>
    const acronyms = languages.map(l => l.acronym)
    const multiRegex = new RegExp(`/${iconKey}_(${acronyms.join('|')})\\.`)
    const multiMatches = resources.filter(r => multiRegex.test(r))
    if (multiMatches.length > 0) {
        const mainLangMatch = multiMatches.find(r => _getImageAcronym(r, languages) === mainLanguageAcronym)
        if (mainLangMatch) return mainLangMatch
        if (process.env.NODE_ENV !== 'production') {
            // eslint-disable-next-line no-console
            console.warn(`menuVisuals: missing "${mainLanguageAcronym}" variant for icon "${iconKey}", using first available`)
        }
        return multiMatches[0]
    }

    return null
}

function _getImageAcronym(imageUrl, languages) {
    const filename = imageUrl.split('/').pop()
    const nameWithoutExt = filename.split('.')[0]
    const match = languages.find(lang => nameWithoutExt.endsWith(`_${lang.acronym}`))
    return match ? match.acronym : null
}

/**
 * Returns true when the item's icon key has per-language variants
 * (e.g. icon_home_en.png, icon_home_es.png). In that case the icon
 * already encodes the label, so callers may skip rendering text.
 */
export function hasLanguageVariantIcon(item, resources, languages) {
    const iconKey = item && item.icon
    if (!iconKey || !languages.length) return false
    const acronyms = languages.map(l => l.acronym)
    const multiRegex = new RegExp(`/${iconKey}_(${acronyms.join('|')})\\.`)
    return resources.some(r => multiRegex.test(r))
}

// =============================================================================
// Style builders (pure functions of layout/styles/ratio + item)
// =============================================================================

/**
 * Outer phone-wrapper style: width, max-width, border, radius, overflow:hidden.
 *
 * @param {number} baseWidth      e.g. 390 (iPhone 16)
 * @param {number} ratio
 */
export function buildPhoneWrapperStyle(baseWidth, ratio) {
    return {
        width: `${baseWidth}px`,
        'max-width': '100%',
        'border-radius': `${32 * ratio}px`,
        border: '1px solid rgba(0,0,0,0.15)',
    }
}

/** Header bar style for the phone screen (primary_color background, scaled height). */
export function buildHeaderStyle(styles, ratio, minHeight = 56) {
    return {
        gap: '8px',
        'min-height': `${minHeight * ratio}px`,
        padding: `${10 * ratio}px ${14 * ratio}px`,
        background: styles.primary_color ? argbToRgba(styles.primary_color) : '#1e1e2e',
        'border-bottom': '1px solid rgba(0,0,0,0.15)',
    }
}

/** Footer bar style for the phone screen. */
export function buildFooterStyle(styles, ratio, minHeight = 56) {
    return {
        'min-height': `${minHeight * ratio}px`,
        padding: `${10 * ratio}px ${14 * ratio}px`,
        background: styles.primary_color ? argbToRgba(styles.primary_color) : '#1e1e2e',
        'border-top': '1px solid rgba(0,0,0,0.15)',
    }
}

/**
 * Device-chrome status bar (sits ABOVE the app header). Same primary_color as
 * the header so it reads as a single nav surface — matching modern iOS apps
 * that extend their nav bar into the status bar area.
 */
export function buildStatusBarStyle(styles, ratio, height, fontSize, horizPadding) {
    return {
        height: `${height * ratio}px`,
        padding: `0 ${horizPadding * ratio}px`,
        background: styles.primary_color ? argbToRgba(styles.primary_color) : '#1e1e2e',
        'font-size': `${fontSize * ratio}px`,
    }
}

/**
 * Device-chrome home indicator (sits BELOW the app footer). Same background
 * as the footer; the pill inside contrasts via navTextColor.
 */
export function buildHomeIndicatorStyle(styles, ratio, height) {
    return {
        height: `${height * ratio}px`,
        background: styles.primary_color ? argbToRgba(styles.primary_color) : '#1e1e2e',
    }
}

/** Pill inside the home indicator — width/height geometry only. */
export function buildHomeIndicatorPillStyle(navTextColor, ratio, width, height) {
    return {
        width: `${width * ratio}px`,
        height: `${height * ratio}px`,
        'border-radius': '999px',
        'background-color': navTextColor,
        opacity: 0.7,
    }
}

/**
 * Body style (scrollable content area). Resolves background by menu type:
 *   - selector / profile: CDN image with background_home as the CSS fallback color
 *   - home:               background_home color, falling back to a CDN image
 */
export function buildBodyStyle(styles, ratio, bodyHeight, menuType, backgroundImageUrl) {
    const style = {
        height: `${bodyHeight * ratio}px`,
        'overflow-y': 'overlay',
        'overflow-x': 'hidden',
    }
    if (menuType === MENU_TYPES.SELECTOR || menuType === MENU_TYPES.PROFILE) {
        style['background-image'] = `url(${backgroundImageUrl})`
        style['background-size'] = 'cover'
        style['background-position'] = 'top center'
        if (styles.background_home) {
            style['background-color'] = argbToRgba(styles.background_home)
        }
    } else if (styles.background_home) {
        style['background-color'] = argbToRgba(styles.background_home)
    } else {
        style['background-image'] = `url(${backgroundImageUrl})`
        style['background-size'] = 'cover'
        style['background-position'] = 'top center'
    }
    return style
}

/**
 * Inline `:style` for a single cell. Handles both special (widget/banner)
 * placeholders and regular icon-background cells, including the native-mode
 * full-width / no-icon variant.
 */
export function buildCellStyle({ item, layout, ratio, isNativeMode, isDragging, isSpecial, iconUrl, fullWidthCols, isMissingIcon }) {
    const rows = item.rows || 1
    const cols = isNativeMode || isSpecial ? (fullWidthCols || layout.columns_number || 1) : (item.width || 1)
    const w = widthFor(cols, layout, ratio)
    // Native rows use a compact fixed height (iOS-like list cells); regular
    // grid cells span their configured layout.height per row.
    const h = isNativeMode ? NATIVE_ROW_HEIGHT * ratio : heightFor(rows, layout, ratio)
    const opacity = (isDragging || !item.published) ? '0.3' : '1'

    if (isSpecial) {
        const paddingH = (layout.padding_horizontal_widget || 0) * ratio
        const paddingV = (layout.padding_vertical_widget || 0) * ratio
        return {
            position: 'relative',
            'border-radius': `${layout.corner_radious_widget || 0}px`,
            margin: `${paddingV}px ${paddingH}px`,
            width: `${w - 2 * paddingH}px`,
            height: `${h - 2 * paddingV}px`,
            display: 'flex',
            'flex-direction': 'column',
            'align-items': 'center',
            'justify-content': 'center',
            border: '2px dashed rgba(128,128,128,0.4)',
            'background-color': 'rgba(128,128,128,0.08)',
            cursor: 'default',
            opacity,
            overflow: 'hidden',
        }
    }

    // Missing-icon cells get a light red tint (no icon background image to mask it).
    let bgColor = isNativeMode ? 'rgba(0,0,0,0.12)' : 'transparent'
    if (isMissingIcon && !isNativeMode) bgColor = 'rgba(239,68,68,0.12)'

    const style = {
        position: 'relative',
        width: `${w}px`,
        height: `${h}px`,
        display: 'flex',
        'justify-content': 'center',
        'align-items': isNativeMode ? 'center' : 'flex-end',
        cursor: isSubmenuItem(item) ? 'pointer' : 'default',
        overflow: 'hidden',
        opacity,
        'background-color': bgColor,
        'border-bottom': isNativeMode ? '1px solid rgba(0,0,0,0.08)' : 'none',
    }
    if (iconUrl) {
        style['background-image'] = `url(${iconUrl})`
        style['background-size'] = '100% 100%'
    }
    return style
}

/** Style for the text overlay inside a regular grid cell. */
export function buildItemTextStyle(styles, ratio, fontName, isNativeMode) {
    return {
        margin: '0',
        'text-align': styles.home_cell_text_align || 'center',
        'font-family': fontName || styles.home_font || 'sans-serif',
        'font-size': `${(styles.home_font_size || 12) * ratio}px`,
        color: isNativeMode
            ? 'rgba(30,30,30,0.9)'
            : (styles.home_cell_text_color ? argbToRgba(styles.home_cell_text_color) : '#ffffff'),
    }
}

/** Padding/margins for the text container that wraps the cell text overlay. */
export function buildTextContainerStyle(layout, ratio, isNativeMode) {
    const pad = `${(layout.text_lateral_margin || 4) * ratio}px`
    if (isNativeMode) {
        return { 'padding-right': pad, 'padding-left': pad }
    }
    return {
        'max-height': `${(layout.text_height || 40) * ratio}px`,
        'margin-bottom': `${(layout.text_bottom_margin || 8) * ratio}px`,
        'padding-right': pad,
        'padding-left': pad,
    }
}

/** Container style for the grid (width, padding, gap). */
export function buildGridContainerStyle(layout, ratio) {
    return {
        width: '100%',
        padding: `${getHomePadding(layout, ratio)}px`,
        gap: `${getGapWidth(layout, ratio)}px`,
    }
}

// =============================================================================
// Guide-line geometry
// =============================================================================

/**
 * Compute absolute pixel positions for the column edges (vertical lines) and
 * row edges (horizontal lines) overlaid on the grid in show-guides mode.
 */
export function buildGuideLines(layout, ratio, totalRows) {
    const padding  = getHomePadding(layout, ratio)
    const gap      = getGapWidth(layout, ratio)
    const colWidth = getColumnWidth(layout, ratio)
    const rowH     = heightFor(1, layout, ratio)
    const cols     = layout.columns_number || 1

    const vertical = []
    for (let c = 0; c < cols; c++) {
        vertical.push(`${padding + c * (colWidth + gap)}px`)
        vertical.push(`${padding + c * (colWidth + gap) + colWidth}px`)
    }
    const horizontal = []
    for (let r = 0; r < totalRows; r++) {
        horizontal.push(`${padding + r * (rowH + gap)}px`)
        horizontal.push(`${padding + r * (rowH + gap) + rowH}px`)
    }
    return { vertical, horizontal }
}

/** Dashed-line gradient pattern used for the guide overlays. */
export function dashPattern(direction) {
    const c = 'rgba(0,0,0,0.35)'
    return `repeating-linear-gradient(to ${direction}, ${c} 0px, ${c} 2px, transparent 2px, transparent 6px, ${c} 6px, ${c} 15px, transparent 15px, transparent 19px)`
}

// =============================================================================
// Font loading
// =============================================================================

const _loadedFonts = new Set()

/**
 * Load a custom font via the FontFace API, deduplicated by name so each font
 * is fetched at most once per page lifetime.
 */
export async function loadMenuFont(fontName, resourcesBaseUrl) {
    if (!fontName || _loadedFonts.has(fontName)) return
    const fontUrl = `${resourcesBaseUrl}/delivery/fonts/${fontName}.ttf`
    const myFont = new FontFace(fontName, `url(${fontUrl})`)
    await myFont.load()
    document.fonts.add(myFont)
    _loadedFonts.add(fontName)
}

/** Test helper — clears the deduplication set between test runs. */
export function _resetLoadedFonts() {
    _loadedFonts.clear()
}
